import React, { useState } from 'react';
import Badge from 'shared/components/UC/Badge';
import Button from 'shared/components/UC/Button';
import {
  ImportIssueCode,
  ImportIssueSeverity,
  ImportSummaryTotals,
} from '../../../types/bulkAssignment';
import {
  IMPORT_ISSUE_CATALOG,
  IMPORT_ISSUE_ORDER,
  SEVERITY_LABEL,
  SEVERITY_VARIANT,
  countLabel,
} from './importIssues';
import styles from './ImportIssuesPanel.module.css';

const SECTIONS: Array<{ severity: ImportIssueSeverity; title: string; caption: string }> = [
  {
    severity: 'error',
    title: 'No se importan',
    caption: 'Estos matches se descartan al aplicar.',
  },
  {
    severity: 'warning',
    title: 'Se importan, pero conviene revisarlos',
    caption: 'Quedan creados y señalados en los listados.',
  },
  {
    severity: 'info',
    title: 'Para tu información',
    caption: 'No hay nada que corregir aquí.',
  },
];

interface ImportIssuesPanelProps {
  totals: ImportSummaryTotals;
  activeFilter: ImportIssueCode | null;
  onFilterChange: (code: ImportIssueCode | null) => void;
}

const ImportIssuesPanel: React.FC<ImportIssuesPanelProps> = ({
  totals,
  activeFilter,
  onFilterChange,
}) => {
  const [open, setOpen] = useState(true);

  const present = IMPORT_ISSUE_ORDER.filter((code) => (totals.issueCounts[code] ?? 0) > 0);

  if (present.length === 0) {
    return (
      <div className={styles.clean}>
        <i className="uc-icon" aria-hidden="true">
          check_circle
        </i>
        <p className="uc-p-body">
          No hay casos que requieran gestión manual. Todas las postulaciones del
          archivo quedan con la cobertura que pide el concurso.
        </p>
      </div>
    );
  }

  return (
    <section className={styles.panel} aria-label="Casos que requieren gestión manual">
      <header className={styles.header}>
        <div>
          <h3 className="uc-h5">Requieren gestión manual</h3>
          <p className={`uc-p-body-small ${styles.caption}`}>
            Ninguno impide importar. Los que quedan pendientes se pueden resolver
            después con Gestionar Evaluadores.
          </p>
        </div>
        <Button
          type="button"
          variant="text"
          size="sm"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          {open ? 'Ocultar detalle' : 'Ver detalle'}
        </Button>
      </header>

      {open && (
        <div className={styles.sections}>
          {SECTIONS.map((section) => {
            const codes = present.filter(
              (code) => IMPORT_ISSUE_CATALOG[code].severity === section.severity
            );
            if (codes.length === 0) return null;
            return (
              <div key={section.severity} className={styles.section}>
                <div className={styles.sectionHead}>
                  <Badge
                    label={SEVERITY_LABEL[section.severity]}
                    variant={SEVERITY_VARIANT[section.severity]}
                  />
                  <span className={`uc-p-body-small ${styles.caption}`}>
                    {section.caption}
                  </span>
                </div>
                <ul className={styles.list}>
                  {codes.map((code) => {
                    const meta = IMPORT_ISSUE_CATALOG[code];
                    const count = totals.issueCounts[code] ?? 0;
                    const isActive = activeFilter === code;
                    return (
                      <li key={code} className={styles.item}>
                        <div className={styles.itemText}>
                          <span className={`uc-p-body ${styles.itemLabel}`}>
                            {meta.label}
                          </span>
                          <span className={`uc-p-body-small ${styles.caption}`}>
                            {meta.hint}
                          </span>
                        </div>
                        <span className={`uc-p-body-small ${styles.count}`}>
                          {countLabel(meta.countBy, count)}
                        </span>
                        <Button
                          type="button"
                          variant="text"
                          size="sm"
                          onClick={() => onFilterChange(isActive ? null : code)}
                        >
                          {isActive ? 'Quitar filtro' : 'Ver'}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ImportIssuesPanel;
